import { Button } from "@/components/ui/button";
import PasswordInput from "../input-password";
import { useContext, useEffect } from "react";
import {
  EditAccountContext,
  EditAccountViewState,
} from "@/pages/containers/editAccount-container";
import { useForm } from "@tanstack/react-form";
import { changePasswordSchema } from "@/lib/form-schema";
import { useMUpdateAccountInfo } from "@/hooks/use-update-accountInfo";
import useAuth from "@/context/auth-provider";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "../error-notification";

const EditAccountPasswordVerify = () => {
  const { authData } = useAuth();
  const updateInfoQuery = useMUpdateAccountInfo();
  const { addError } = useNotificationError();
  const { setViewState, onOpenChange, setPayloadData, payloadData } =
    useContext(EditAccountContext);

  const form = useForm({
    defaultValues: {
      password: "",
    },
    onSubmit: async ({ value }) => {
      updateInfoQuery.mutate(
        {
          email: payloadData.email,
          phone: payloadData.phone,
          firstname: payloadData.firstname,
          lastname: payloadData.lastname,
          groupId: payloadData.groupId,
          memberId: payloadData.memberId,
          username: payloadData.username,
          password: value.password,
          accessToken: authData?.user.access_token,
        },
        {
          onSuccess: (data) => {
            if (data.success) {
              setPayloadData({
                ...payloadData,
                signature: data.signature,
                password: value.password,
              });
              setViewState(EditAccountViewState.SEND_OTP);
              addError({
                message: data.message,
                type: ErrorNotificationType.INFO,
              });
            } else {
              onOpenChange(false);
              setViewState(EditAccountViewState.ENABLE);
              addError({
                message: data.message,
                type: ErrorNotificationType.ERROR,
              });
            }
          },

          onError: (error) => {
            onOpenChange(false);
            setViewState(EditAccountViewState.ENABLE);
            addError({
              message: error.message,
              type: ErrorNotificationType.ERROR,
            });
          },
        },
      );
    },
  });

  useEffect(() => {
    return () => {
      form.reset();
    };
  }, [form]);

  return (
    <form
      className="flex flex-col items-center gap-7"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <p>Please enter your current password to update your profile</p>
      <form.Field
        name="password"
        validators={{
          onBlur: ({ value }) => {
            const result = changePasswordSchema.shape.password.safeParse(value);
            return result.success ? undefined : result.error.errors[0].message;
          },
        }}
      >
        {(field) => (
          <div className="w-full">
            <PasswordInput
              id="password"
              placeholder="Password"
              value={field.state.value}
              onChange={(newValue) => field.handleChange(newValue)}
              onBlur={() => field.handleBlur()}
              error={field.state.meta.errors[0]}
            />
          </div>
        )}
      </form.Field>

      <div className="flex w-full items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          className="min-w-32"
          onClick={() => {
            form.reset();
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="min-w-32"
          disabled={updateInfoQuery.isPending}
        >
          {updateInfoQuery.isPending ? "Verifying..." : "Verify"}
        </Button>
      </div>
    </form>
  );
};

export default EditAccountPasswordVerify;
